import { useDispatch, useSelector } from "react-redux";
import ChatInput from "../../AmplitudeAI/Chat/ChatInput";
import PageDescription from "../../DataAnalyst/sections/PageDescription";
import MixesFileUpload from "./FileUpload";
import { sendMessage } from "../../../../redux/actions";
import { useCallback, useState, useEffect } from "react";
import { showToast } from "../../../../helper/ShowToast";
import { useLocation, useNavigate } from "react-router-dom";
import { PricingPlanModal } from "../../../../components/PricingPlanModal";
import { CreditPlanModal } from "../../../../components/CreditPlanModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as actions from "../../../../redux/actions/audioEngineer";
import Multitrack from "./MultiTrack";
import Settings from "../AudioEngineer/sections/Settings";
import MixesLibrary from "./MixesLibrary/MixesLibrary";
import AppTour from "../../../../components/AppTour/AppTour";

const steps = [
  {
    target: "[data-tour='upload-section']",
    content: "Upload your tracks - Start by uploading your tracks. You can upload individual stems (2-32 tracks) for a more detailed mix. Our AI will analyze your audio to prepare it for mixing.",
    placement: "top",
    title: "Upload Your Tracks"
  },
  {
    target: "[data-tour='amplitude-ai-chat']",
    content: "Amplitude AI Chat - Get personalized mixing recommendations, release strategies. Just ask Amplitude AI for insights and guidance to make the most of your music.",
    placement: "top",
    title: "Amplitude AI Chat"
  },
  {
    target: "[data-tour='mixes-library']",
    content: "Mixes Library - Store, Share, Download and manage your mixed tracks, keep everything organized and easily accessible for master release.",
    placement: "top",
    title: "Mixes Library"
  }
];

export default function MixingEngineer() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pageTab = params.get("tab");
    const [runTour, setRunTour] = useState(false)
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [newFile, setNewFile] = useState(true)
    const [showTrackList, setShowTrackList] = useState(false)
    const [showSettings, setShowSettings] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showMixesFileUpload, setShowMixesFileUpload] = useState(false);

    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const { uploadedData } = useSelector((state) => state.audioEngineer);

    const localGenre = localStorage.getItem('genre');
    const localSampleRate = localStorage.getItem('sampleRate');
    const [genre, setGenre] = useState(localGenre || "ROCK_INDIE");
    const [sampleRate, setSampleRate] = useState(localSampleRate || "48000");

    const handleUploadNew = () => {
        setNewFile(true);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        if (pageTab === "multitrack") {
            setShowMixesFileUpload(false);
            setShowTrackList(true)
        }
        if (pageTab === "settings") {
            setShowMixesFileUpload(false);
            setShowSettings(true)
        }
    }, [pageTab]);

    useEffect(() => {
        if(location.hash === "#start-tour"){
            setRunTour(true)
        }
    }, [location.hash])

    const handleFileSelect = useCallback(async (file) => {
        setNewFile(false);
        setShowMixesFileUpload(false);
        setShowSettings(true);

        const fileArray = Array.isArray(file) ? file : (file && file.length ? Array.from(file) : [file]);

        // Validate files
        const validFiles = fileArray.filter(f => f && f instanceof File);

        if (validFiles.length === 0) {
            showToast("error", "No valid files selected");
            return;
        }

        // Pass array of File objects directly (NOT FormData)
        dispatch(actions.uploadAndGetPreview(
            toast,
            validFiles,
            setIsUploading,
            'mixing-engineer',
            false,
            '',
            navigate
        ));
        navigate('/admin/urstudio/mixing-engineer?tab=settings')
    }, [dispatch]);

    const handleSettingsForPreview = () => {
        localStorage.setItem('genre', genre);
        localStorage.setItem('sampleRate', sampleRate);
        setShowSettings(false)
        setShowTrackList(true)
        navigate('/admin/urstudio/mixing-engineer?tab=multitrack')
    }

    const getSettings = (e) => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    // Listen for global event dispatched by MultiTrack when user requests upload while mixPreview+uploadedData exist
    useEffect(() => {
        // dispatch(actions.clearUploadData());
        const handler = () => {
            // clear reducers and show dropzone
            if (pageTab !== 'settings') {
                // dispatch(actions.clearUploadData());
                dispatch(actions.uploadData({}));
                dispatch(actions.mixPreviewData({}));
                setShowMixesFileUpload(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };
        window.addEventListener('urstudio:show-mixes-upload', handler);
        return () => window.removeEventListener('urstudio:show-mixes-upload', handler);
    }, [dispatch, pageTab]);

    // Check route and clear data on mixing-engineer page
    useEffect(() => {
        if (location.pathname.includes('mixing-engineer') && pageTab !== 'multitrack' && pageTab !== 'settings') {
            dispatch(actions.uploadData({}));
            dispatch(actions.mixPreviewData({}));
            setShowMixesFileUpload(true);
            setShowSettings(false);
        }
    }, [location.pathname, dispatch]);

    const handleSend = (input) => {
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);
            return;
        }

        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true);
            return;
        }

        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    return (
        <div className="DataAnalyst-page py-5">
            <AppTour
                tourSteps={steps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <PageDescription
                    title="Your Mixing Engineer"
                    description="Create studio-quality mixes in minutes — powered by intelligent, genre-aware AI."
                />
                {(showMixesFileUpload || (!isUploading && !uploadedData.data)) && !showSettings && (
                    <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl">
                        <MixesFileUpload onContinue={handleFileSelect} />
                    </div>
                )}

                {/* NEW: Show settings after upload */}
                {showSettings && uploadedData?.data?.[0]?.operation === 'mixing-engineer' && (
                    <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl">
                        <div className="bg-white flex flex-col items-center gap-4 relative max-w-[500px] mx-auto rounded-2xl p-4 lg:p-5">
                            <Settings
                                genre={genre}
                                setGenre={setGenre}
                                sampleRate={sampleRate}
                                setSampleRate={setSampleRate}
                                showLoudness={false}
                            />
                            {/* <hr className="border-gray-700 w-full" /> */}
                            {uploadedData?.data?.length > 0 && (
                                <div className="w-full mt-4 space-y-3">
                                    {uploadedData.data.map((file, index) => {

                                        let progress = 30;
                                        let label = "30%";

                                        if (file.roexStatus === "LOCAL_UPLOADING") {
                                            progress = 15;
                                            label = "15%";
                                        }

                                        if (file.roexStatus === "QUEUED_FOR_ROEX") {
                                            progress = 40;
                                            label = "40%";
                                        }

                                        if (file.roexStatus === "UPLOADING_TO_ROEX") {
                                            progress = 60;
                                            label = "60%";
                                        }

                                        if (file.roexStatus === "UPLOADED_TO_ROEX") {
                                            progress = 100;
                                            label = "100%";
                                        }

                                        if (file.roexStatus === "FAILED") {
                                            progress = 100;
                                            label = "Failed";
                                        }

                                        return (
                                            <div key={index} className="w-full">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="truncate max-w-[70%]">
                                                        {file.filename}
                                                    </span>
                                                    <span>{label}</span>
                                                </div>

                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full transition-all duration-700 ${file.roexStatus === "FAILED"
                                                                ? "bg-red-500"
                                                                : `${file.roexStatus === "UPLOADED_TO_ROEX" ? "bg-theme_clr2" : "bg-theme_clr2 animated-pulse"}`
                                                            }`}
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <button
                                onClick={handleSettingsForPreview}
                                className={`mt-2 w-full max-w-md bg-theme_clr2 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium ${isUploading ? 'opacity-70' : 'opacity-100'}`}
                                disabled={isUploading}
                            >
                                {isUploading ? 'Processing your files...' : 'Save Settings & Start Mixing'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Multitrack is visible when showing the upload area or when track list should be shown */}
                {(showTrackList && uploadedData.data) && !showSettings && (
                    <Multitrack onUploadTracks={handleUploadNew} />
                )}
                <div className="mt-4" data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={'Plan your next move with your mixed tracks.'}
                        inputPlaceholder={"Let Amplitude AI design the perfect release strategy."}
                        onSend={handleSend}
                        isDisabled={showLoad}
                    />
                </div>
                {(showMixesFileUpload || (!isUploading && !uploadedData.data)) && !showSettings && (
                    <div className="bg-theme_clr1 text-white rounded-xl p-4 mt-4" data-tour="mixes-library">
                        <MixesLibrary addNew={handleUploadNew} handleSettings={getSettings} />
                    </div>
                )}
            </div>
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={window.location.origin + "/admin/urstudio/mixing-engineer"}
            />
            <CreditPlanModal
                isOpen={showCreditPlanModal}
                onClose={() => setShowCreditPlanModal(false)}
            />
        </div>
    )
}